import { Action, ActionType } from "../../../AppDataProvider/actions";
import { AppContext, BookingData } from "../../../AppDataProvider/AppDataProvider";
import { GetServiceResourcesQuery, ResourceSelectionMode, useGetServiceResourcesQuery } from "../../../GeneratedGraphQL";
import React, { useContext, useEffect } from "react";
import { absoluteCentre } from "../../../common/styles";
import { LoadingPill as BaseLoadingPill } from "../../../common/components";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { PartRequired } from "../../../utils/types";
import { requireBookingDataFields } from "../../../utils";
import { StepContent } from "../StepContent/StepContent";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

/**
 * The data layer used for loading resources for a
 * specific service when a booking tool id is not
 * available.
 */
export function GenericResourceData() {
  const {
    t
  } = useTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  requireBookingDataFields<PartRequired<BookingData, "organisation" | "service">>(appData.bookingData, ["service", "organisation"]);
  const {
    organisation,
    service,
    resourceIds
  } = appData.bookingData;
  const resourceSelectionBeforeAvailability = organisation.resourceSelectionBeforeAvailability;
  const {
    data,
    loading,
    error,
    fetchMore
  } = useGetServiceResourcesQuery({
    fetchPolicy: "network-only",
    variables: {
      id: service._id
    }
  });
  useEffect(() => {
    // All the error dialogs for this page are the same,
    // except for their exact error content.
    // Ignoring onClick coverage because it's not practical to test, and it
    // would be redundant to test a native function anyway.
    const showModalDialog = (content: string): Action => ({
      payload: {
        buttonLabel: t("tryAgain"),
        content,
        icon: faExclamationTriangle,
        onClick: /* istanbul ignore next */() => location.reload(),
        title: t("serverError")
      },
      type: ActionType.SHOW_MODAL_DIALOG
    });
    if (!loading) {
      if (error) {
        console.error({
          error
        });
        appDataDispatch(showModalDialog(t("errors.generic")));
      } else if (!data || !data.service) {
        appDataDispatch(showModalDialog(t("errors.noServiceFound")));
      } else if (!data.service.resources?.edges.length) {
        appDataDispatch(showModalDialog(t("errors.noResources")));
      }
      // If we're in resource group selection mode, but there are no resource
      // groups amongst the returned resources, throw an error.
      if (appData.bookingTool.resourceSelectionMode === ResourceSelectionMode.Group && !isAtLeastOneResourceGroup(data)) {
        appDataDispatch(showModalDialog(t("errors.noResourceGroups")));
      }
    }
  }, [appDataDispatch, data, error, loading, t]);
  if (loading) return <LoadingPill />;
  if (error || !data?.service?.resources?.edges.length) return null;
  if (data.service.resources.pageInfo.hasNextPage) {
    void fetchMore({
      variables: {
        cursor: data.service.resources.pageInfo.endCursor,
        id: service._id
      }
    });
    return <LoadingPill />;
  }
  const resources = resourceSelectionBeforeAvailability || !resourceIds ? data.service.resources : {
    ...data.service.resources,
    edges: data.service.resources.edges.filter(({
      node
    }) => {
      return resourceIds && resourceIds.includes(node._id);
    })
  };
  return <StepContent resources={resources} data-sentry-element="StepContent" data-sentry-component="GenericResourceData" data-sentry-source-file="GenericResourceData.tsx" />;
}
const LoadingPill = styled(BaseLoadingPill)`
  ${absoluteCentre}
`;
function isAtLeastOneResourceGroup(data: GetServiceResourcesQuery | undefined) {
  return data?.service?.resources?.edges && !data.service.resources.pageInfo.hasNextPage && data.service.resources.edges.flatMap(edge => edge.node.groups.edges).length > 0;
}
export default GenericResourceData;